import { Fragment, useState } from 'react'
import { ChevronDown, ChevronRight, LineChart } from 'lucide-react'
import { resolveBookIdentity } from '../lib/bookLogoRegistry.js'
import { getTeamDisplayName, getTeamLogo } from '../lib/logoMaps.js'
import { buildSelectionMetrics, parseEventTeams } from '../lib/liveOddsMarketTerminalModelV1.js'
import LiveOddsVNextMultiBookHistoryChart from './LiveOddsVNextMultiBookHistoryChart.jsx'
import './LiveOddsVNextEventBoard.css'

function formatOdds(value, format) {
  if (value === null || value === undefined || value === '') return '—'
  const number = Number(value)
  if (!Number.isFinite(number)) return '—'
  if (format === 'decimal') return (number >= 100 ? 1 + number / 100 : 1 + 100 / Math.abs(number)).toFixed(2)
  const rounded = Math.round(number)
  return rounded > 0 ? `+${rounded}` : String(rounded)
}

function formatLine(value, selection) {
  const number = Number(value)
  if (!Number.isFinite(number)) return ''
  if (/^over$/i.test(selection)) return `O ${number}`
  if (/^under$/i.test(selection)) return `U ${number}`
  return number > 0 ? `+${number}` : String(number)
}

function percent(value) {
  return Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '—'
}

function BookLogo({ book }) {
  const [failed, setFailed] = useState(false)
  const identity = resolveBookIdentity(book)
  return <div className="lovxt-book-identity" title={identity.displayName}>
    {identity.logoUrl && !failed
      ? <img src={identity.logoUrl} alt={identity.displayName} onError={() => setFailed(true)} />
      : <b>{identity.displayName.slice(0, 3)}</b>}
    <span>{identity.displayName}</span>
  </div>
}

function TeamIdentity({ team, league, side }) {
  const logo = getTeamLogo(team, league)
  const name = getTeamDisplayName(team, league) || team || 'Unknown team'
  return <div className="lovxt-team">
    <div className="lovxt-team-logo">{logo ? <img src={logo} alt={`${name} logo`} /> : <span title="Canonical team logo unavailable">{String(team || '?').slice(0, 3)}</span>}</div>
    <div><small>{side}</small><strong>{name}</strong><code>{team}</code></div>
  </div>
}

function PriceCell({ cell, oddsFormat, onInspect }) {
  if (!cell) return <td className="lovxt-price empty">—</td>
  return <td className={`lovxt-price ${cell.best ? 'best' : ''}`}>
    <button type="button" onClick={() => onInspect(cell.row)} title="Inspect raw row, history, provenance, and betslip link">
      {cell.row.line != null && <small>{formatLine(cell.row.line, cell.row.selection)}</small>}
      <strong>{formatOdds(cell.row.american, oddsFormat)}</strong>
      {Number.isFinite(cell.evPercent) && cell.evPercent > 0 && <em>+{cell.evPercent.toFixed(1)}%</em>}
    </button>
  </td>
}

function Metric({ value, detail, tone = '' }) {
  return <td className={`lovxt-metric ${tone}`}><strong>{value}</strong><small>{detail}</small></td>
}

function MarketRows({ event, market, books, oddsFormat, fairSettings, onInspect, onChart }) {
  return <>
    <tr className="lovxt-market-row"><td colSpan={books.length + 7}>
      <strong>{market.betType}</strong><span>{market.period}{market.line == null ? '' : ` · line ${market.line}`}</span>
      <b>{market.books.length} books · {market.rowCount} raw rows</b>
    </td></tr>
    {market.selections.map((selection) => {
      const metrics = buildSelectionMetrics(market, selection, books.length, fairSettings)
      const firstCell = Object.values(market.cells[selection] || {})[0]
      const breadthPercent = metrics.breadth.universe > 0 ? metrics.breadth.books / metrics.breadth.universe : null
      const derivedDetail = metrics.comparable ? null : metrics.integrityStatus.replaceAll('_', ' ')
      return <tr key={`${market.key}|${selection}`} className="lovxt-offer-row">
        <th className="lovxt-selection">
          <button type="button" className="lovxt-chart-button" onClick={() => onChart({ event, market, selection })} title="Open real multi-book odds history"><LineChart size={14} /></button>
          <span><strong>{selection}</strong><small>{formatLine(firstCell?.row?.line, selection)} · {market.period}</small></span>
        </th>
        <Metric value={formatOdds(metrics.bestAmerican, oddsFormat)} detail={derivedDetail || `${metrics.bestBooks.length} best`} tone="best" />
        <Metric value={formatOdds(metrics.average.american, oddsFormat)} detail={derivedDetail || `${metrics.average.bookCount} raw books`} />
        <Metric value={formatOdds(metrics.fair?.american, oddsFormat)} detail={derivedDetail || `${metrics.fair?.bookCount || 0} de-vigged`} tone="fair" />
        <Metric value={percent(metrics.hold.probability)} detail={derivedDetail || `${metrics.hold.bookCount} complete`} tone={metrics.hold.probability < 0 ? 'negative' : ''} />
        <Metric value={`${metrics.breadth.books}/${metrics.breadth.universe}`} detail={percent(breadthPercent)} />
        <Metric value={String(metrics.depth.marketRows)} detail={`${metrics.depth.selectionRows} selection`} />
        {books.map((book) => <PriceCell key={book} cell={market.cells[selection]?.[book]} oddsFormat={oddsFormat} onInspect={onInspect} />)}
      </tr>
    })}
  </>
}

function EventRows({ event, books, expanded, oddsFormat, fairSettings, onInspect, onChart, historyTarget, onToggle, onCloseHistory }) {
  const teams = parseEventTeams(event.event)
  const primary = event.primaryMarkets.map((item) => item.market).filter(Boolean)
  const markets = expanded ? [...primary, ...event.alternateMarkets] : primary
  return <Fragment>
    <tr className="lovxt-event-row"><td colSpan={books.length + 7}><div className="lovxt-event-header">
      <div className="lovxt-matchup"><TeamIdentity team={teams.away} league={event.league} side="Away" /><span className="lovxt-at">@</span><TeamIdentity team={teams.home} league={event.league} side="Home" /></div>
      <div className="lovxt-event-meta"><b>{event.league}</b><time>{event.etDate || 'date unknown'} · {event.etTime || 'time unknown'}</time><span>{event.primaryBooks.length} books · {event.rowCount} raw offers</span></div>
    </div></td></tr>
    {markets.map((market) => <MarketRows key={market.key} event={event} market={market} books={books} oddsFormat={oddsFormat} fairSettings={fairSettings} onInspect={onInspect} onChart={onChart} />)}
    {historyTarget?.event?.key === event.key && <tr className="lovxt-history-row"><td colSpan={books.length + 7}><LiveOddsVNextMultiBookHistoryChart market={historyTarget.market} selection={historyTarget.selection} onClose={onCloseHistory} /></td></tr>}
    <tr className="lovxt-event-footer"><td colSpan={books.length + 7}><span>{event.availablePrimaryCount}/3 main market families · {event.alternateMarkets.length} additional families retained</span>
      {event.alternateMarkets.length > 0 && <button type="button" onClick={onToggle}>{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{expanded ? 'Hide additional markets' : `Show all ${event.alternateMarkets.length} additional markets`}</button>}
    </td></tr>
  </Fragment>
}

export default function LiveOddsVNextEventBoard({ events, books, oddsFormat, fairSettings, onInspect }) {
  const [expandedEvents, setExpandedEvents] = useState(() => new Set())
  const [historyTarget, setHistoryTarget] = useState(null)
  const toggleEvent = (eventKey) => setExpandedEvents((current) => {
    const next = new Set(current)
    if (next.has(eventKey)) next.delete(eventKey)
    else next.add(eventKey)
    return next
  })
  return <section className="lovxt-board" aria-label="PickTheOdds-style married sportsbook market terminal">
    <div className="lovxt-definition"><b>Customer odds screen</b><span>BEST = highest payout · AVG = weighted raw implied average · FAIR = de-vigged consensus · VIG = complete-market hold · BREADTH = books carrying the offer · DEPTH = retained raw rows. Every sportsbook column is the signed release; swipe horizontally without losing the offer identity.</span></div>
    <div className="lovxt-scroll"><table className="lovxt-table">
      <thead><tr>
        <th className="lovxt-selection-col">Offer</th>
        <th title="Highest customer payout among selected sportsbooks">Best</th>
        <th title="Weighted mean of raw implied probabilities before removing vig">Avg</th>
        <th title="Consensus probability after each complete book market is de-vigged">Fair</th>
        <th title="Weighted average overround among complete sportsbook markets">Vig</th>
        <th title="Sportsbooks carrying this selection / selected sportsbook universe">Breadth</th>
        <th title="Raw rows retained in this married market family">Depth</th>
        {books.map((book) => <th key={book} className="lovxt-book-col"><BookLogo book={book} /></th>)}
      </tr></thead>
      <tbody>{events.map((event) => <EventRows key={event.key} event={event} books={books} expanded={expandedEvents.has(event.key)} oddsFormat={oddsFormat} fairSettings={fairSettings} onInspect={onInspect} onChart={setHistoryTarget} historyTarget={historyTarget} onCloseHistory={() => setHistoryTarget(null)} onToggle={() => toggleEvent(event.key)} />)}</tbody>
    </table></div>
  </section>
}
